import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FolderKanban, Wallet, TrendingDown, Sparkles, Plus, ArrowUpRight, FileDown } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { auditActionLabel, auditEntityLabel } from '@/lib/auditLabels';
import { Button } from '@/components/ui/button';
import QuotaBanner from '@/components/app/QuotaBanner';
import ActivationChecklist from '@/components/app/ActivationChecklist';
import PWAInstallBanner from '@/components/PWAInstallBanner';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function KpiCard({ icon: Icon, label, value, hint, testId }) {
    return (
        <div className="card-soft p-6" data-testid={testId}>
            <div className="flex items-center justify-between">
                <div className="eyebrow">{label}</div>
                <div className="h-9 w-9 rounded-lg bg-primary-light text-primary flex items-center justify-center">
                    <Icon size={16} strokeWidth={1.6} />
                </div>
            </div>
            <div className="mt-4 font-heading text-3xl tracking-tight">{value}</div>
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
        </div>
    );
}

const formatCurrency = (n, c = 'XOF') => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n || 0) + ' ' + c;

export default function Dashboard() {
    const { t, lang } = useLang();
    const { profile } = useAuth();
    const { currentWorkspace, workspaces, loading: wsLoading } = useWorkspace();
    const navigate = useNavigate();
    const [stats, setStats] = useState({ projects: 0, budget: 0, spent: 0, alerts: 0 });
    const [recentProjects, setRecentProjects] = useState([]);
    const [recentAudit, setRecentAudit] = useState([]);
    const [exporting, setExporting] = useState(false);

    const handleExportPdf = async () => {
        if (!currentWorkspace) return;
        setExporting(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
                toast.error('Session expirée');
                setExporting(false);
                return;
            }
            const resp = await fetch(`${API}/exports/dashboard/${currentWorkspace.id}.pdf`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (!resp.ok) {
                const t = await resp.text();
                throw new Error(t.slice(0, 200) || `HTTP ${resp.status}`);
            }
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `dashboard_${currentWorkspace.name || 'espace'}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Dashboard exporté en PDF');
        } catch (e) {
            toast.error(`Export échoué : ${e.message || e}`);
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        if (!wsLoading && workspaces.length === 0) {
            navigate('/app/onboarding', { replace: true });
        }
    }, [wsLoading, workspaces, navigate]);

    useEffect(() => {
        if (!currentWorkspace || !profile) return;
        const wid = currentWorkspace.id;
        (async () => {
            // Activité récente : chaque utilisateur ne voit que SA propre activité,
            // sauf le Super Admin qui voit toute l'activité de l'espace.
            let auditQuery = supabase.from('audit_logs').select('id,action,entity_type,user_id,created_at,new_value').eq('workspace_id', wid);
            if (!profile.is_super_admin) auditQuery = auditQuery.eq('user_id', profile.id);
            const [{ data: projects }, { data: budgets }, { data: expenses }, { data: audits }] = await Promise.all([
                supabase.from('projects').select('id,name,code,status,progress,budget_total,currency,industry,created_at').eq('workspace_id', wid).is('deleted_at', null).order('created_at', { ascending: false }).limit(5),
                supabase.from('budget_lines').select('amount_planned').eq('workspace_id', wid),
                supabase.from('expenses').select('amount').eq('workspace_id', wid),
                auditQuery.order('created_at', { ascending: false }).limit(10),
            ]);
            const totalBudget = (budgets || []).reduce((s, b) => s + Number(b.amount_planned || 0), 0);
            const totalSpent = (expenses || []).reduce((s, e) => s + Number(e.amount || 0), 0);
            const totalProjectsRes = await supabase.from('projects').select('id', { count: 'exact', head: true }).eq('workspace_id', wid).is('deleted_at', null);
            // Enrich audits with author name (one query for all unique user_ids)
            const userIds = Array.from(new Set((audits || []).map(a => a.user_id).filter(Boolean)));
            let nameById = {};
            if (userIds.length) {
                const { data: profs } = await supabase.from('profiles').select('id,full_name,email').in('id', userIds);
                nameById = Object.fromEntries((profs || []).map(p => [p.id, p.full_name || p.email]));
            }
            setStats({
                projects: totalProjectsRes.count || 0,
                budget: totalBudget,
                spent: totalSpent,
                alerts: 0,
            });
            setRecentProjects(projects || []);
            setRecentAudit((audits || []).map(a => ({ ...a, author: nameById[a.user_id] || '—' })));
        })();
    }, [currentWorkspace, profile]);

    if (!currentWorkspace) {
        return (
            <div className="card-soft p-10 text-center">
                <h2 className="font-heading text-3xl mb-3">{t('common.no_workspace')}</h2>
                <p className="text-foreground/70 mb-6">Créez votre premier espace client pour commencer.</p>
                <Link to="/app/onboarding"><Button className="bg-primary hover:bg-primary-hover">{t('common.create_workspace')}</Button></Link>
            </div>
        );
    }

    return (
        <div className="space-y-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">{currentWorkspace.name}</div>
                    <h1 className="font-heading text-4xl">{t('dashboard.welcome')}, {(profile?.full_name || '').split(' ')[0] || ''}</h1>
                    <p className="text-foreground/70 mt-2">{t('dashboard.overview')}</p>
                </div>
                <Link to="/app/projects?new=1">
                    <Button className="bg-primary hover:bg-primary-hover text-primary-foreground" data-testid="new-project-btn">
                        <Plus size={16} className="mr-2" /> {t('dashboard.create_project')}
                    </Button>
                </Link>
                <Button
                    variant="outline"
                    onClick={handleExportPdf}
                    disabled={exporting}
                    data-testid="dashboard-export-pdf-btn"
                    className="ml-2"
                >
                    <FileDown size={16} className="mr-2" />
                    {exporting ? 'Export…' : 'Exporter PDF'}
                </Button>
            </div>

            <QuotaBanner />
            <ActivationChecklist />

            <PWAInstallBanner variant="inline" storageKey="av-pwa-dash-dismissed" />

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <KpiCard icon={FolderKanban} label={t('dashboard.kpi_projects')} value={stats.projects} testId="kpi-projects" />
                <KpiCard icon={Wallet} label={t('dashboard.kpi_budget')} value={formatCurrency(stats.budget)} testId="kpi-budget" />
                <KpiCard icon={TrendingDown} label={t('dashboard.kpi_spent')} value={formatCurrency(stats.spent)} testId="kpi-spent" />
                <KpiCard icon={Sparkles} label={t('dashboard.kpi_alerts')} value={stats.alerts} testId="kpi-alerts" />
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 card-soft p-7">
                    <div className="flex items-center justify-between mb-5">
                        <h2 className="font-heading text-2xl">{t('dashboard.recent_projects')}</h2>
                        <Link to="/app/projects" className="text-sm text-primary hover:underline">
                            {t('common.actions')} <ArrowUpRight size={14} className="inline -mt-0.5" />
                        </Link>
                    </div>
                    {recentProjects.length === 0 ? (
                        <p className="text-foreground/60 py-10 text-center" data-testid="empty-projects">{t('dashboard.no_projects')}</p>
                    ) : (
                        <div className="divide-y divide-border">
                            {recentProjects.map((p) => (
                                <Link key={p.id} to={`/app/projects/${p.id}`} className="flex items-center justify-between py-4 hover:bg-surface-muted -mx-3 px-3 rounded-md transition-colors" data-testid={`recent-project-${p.id}`}>
                                    <div className="min-w-0">
                                        <div className="font-medium truncate">{p.name}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{p.industry || '—'} · {p.code || p.id.slice(0, 8)}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-mono">{p.progress || 0}%</div>
                                        <span className="phase-badge bg-primary-light text-primary">{p.status}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
                <div className="card-soft p-7" data-testid="recent-activity-card">
                    <h2 className="font-heading text-2xl mb-5">{t('dashboard.recent_audit')}</h2>
                    {recentAudit.length === 0 ? (
                        <p className="text-foreground/60 py-6 text-center text-sm">{t('audit.empty')}</p>
                    ) : (
                        <ul className="space-y-4 text-sm">
                            {recentAudit.map((a) => (
                                <li key={a.id} className="flex items-start gap-3" data-testid={`recent-audit-${a.id}`}>
                                    <div className="h-2 w-2 rounded-full bg-secondary mt-2 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium truncate">{auditActionLabel(a.action, lang)}</div>
                                        <div className="text-xs text-muted-foreground truncate">
                                            par <span className="text-foreground/80">{a.author}</span> · {auditEntityLabel(a.entity_type, lang)}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground/80 mt-0.5">{new Date(a.created_at).toLocaleString('fr-FR')}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
